"use client";

import {
  Ticket,
  Layers,
  FileJson,
  NotebookPen,
  ScanText,
  Calendar,
  ShieldCheck,
  FileText,
  Loader2,
} from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/shadcn/badge";
import { InboxUseCase } from "../types";
import { INBOX_USE_CASES } from "../config";
import { StepLayout } from "./HowItWorksSteps";

interface UseCaseStepProps {
  onSelect: (useCase: InboxUseCase | null) => void;
  onNext: (override?: "skip_context") => void;
  onBack: () => void;
  canProceed: boolean;
  isLoading?: boolean;
  loadingUseCase?: InboxUseCase | null;
}

const IconMap = {
  Ticket,
  Layers,
  FileJson,
  NotebookPen,
  ScanText,
  Calendar,
  ShieldCheck,
  FileText,
};

export const UseCaseStep = ({
  onSelect,
  onNext,
  onBack,
  canProceed,
  isLoading,
  loadingUseCase,
}: UseCaseStepProps) => {
  return (
    <StepLayout
      onNext={() => {
        onSelect(null);
        onNext("skip_context");
      }}
      onBack={onBack}
      disabled={!canProceed || isLoading}
      nextLabel="Start from scratch"
      nextVariant="outline"
    >
      <div className="text-center space-y-2 mb-6">
        <motion.h2
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="text-lg font-medium text-foreground"
        >
          What are you looking to automate?
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="text-sm text-muted-foreground max-w-[370px] mx-auto"
        >
          Accelerate setup with pre-configured agents designed for common
          operational challenges.
        </motion.p>
      </div>

      <div className="w-full max-w-xl space-y-3 pb-2">
        {INBOX_USE_CASES.map((useCase, index) => {
          const Icon = IconMap[useCase.icon as keyof typeof IconMap];
          const isThisLoading = isLoading && loadingUseCase === useCase.id;

          const isDisabled = useCase.comingSoon;

          return (
            <motion.button
              key={useCase.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: isDisabled ? 0.6 : 1 }}
              transition={{
                delay: 0.4 + index * 0.1,
                duration: 0.4,
              }}
              whileTap={!isLoading && !isDisabled ? { scale: 0.98 } : undefined}
              onClick={() => {
                if (isDisabled) return;
                onSelect(useCase.id);
              }}
              className={cn(
                "w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all border",
                isDisabled
                  ? "cursor-not-allowed"
                  : isLoading
                    ? "opacity-70 cursor-not-allowed"
                    : "cursor-pointer hover:bg-muted/50",
                isThisLoading
                  ? "border-primary/30 bg-primary/5"
                  : "bg-white shadow-sm"
              )}
            >
              <div
                className={cn(
                  "flex-shrink-0 h-14 w-14 rounded-sm border border-border flex items-center justify-center",
                  useCase.iconBg
                )}
              >
                {isThisLoading ? (
                  <Loader2
                    size={26}
                    strokeWidth={1.5}
                    className={cn(useCase.iconColor, "animate-spin")}
                  />
                ) : (
                  Icon && (
                    <Icon
                      size={26}
                      strokeWidth={1.5}
                      className={useCase.iconColor}
                    />
                  )
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-primary font-medium text-base">
                    {useCase.name}
                  </h3>
                  {useCase.comingSoon && (
                    <Badge
                      variant="secondary"
                      className="text-[10px] px-1.5 py-0"
                    >
                      Enterprise
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {useCase.description}
                </p>
              </div>
            </motion.button>
          );
        })}
      </div>
    </StepLayout>
  );
};
